'use server'

import { GROUP_PHOTOS } from '@/shared/constants/backend-api'
import { executeApiRequest } from '@/shared/lib/helpers/actions.helpers'
import { REQUEST_TYPE } from '@/shared/types/action-helper.type'
import { ApiResponse } from '@/shared/types/backend-response.type'

export interface GroupPhoto {
  id: number
  groupId: string
  uploadedByUserId?: number | null
  uploadedByUser?: { id: number; fullname: string | null } | null
  imageUrl: string
  thumbnailUrl: string
  mediaType: 'image' | 'video' | 'document'
  takenAt: string | null
  uploadedAt: string
  description?: string | null
  tags?: string[] | null
  latitude?: number | null
  longitude?: number | null
  duration?: number | null
  videoCodec?: string | null
  videoResolution?: string | null
  fileSize?: number | null
  isDeleted: boolean
  createdAt: string
  updatedAt: string
}

export interface GroupPhotoFilterOptions {
  mediaType?: 'image' | 'video' | 'document'
  includeDeleted?: boolean
  dateFrom?: string
  dateTo?: string
  /** e.g. ['Confidential'] to show only confidential photos */
  tags?: string[]
}

export async function getPhotosByGroupId(
  groupId: string,
  filters?: GroupPhotoFilterOptions
): Promise<ApiResponse<GroupPhoto[]>> {
  try {
    const params: Record<string, string> = {}
    if (filters?.mediaType) params.mediaType = filters.mediaType
    if (filters?.includeDeleted) params.includeDeleted = 'true'
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom
    if (filters?.dateTo) params.dateTo = filters.dateTo
    if (filters?.tags?.length) params.tags = filters.tags.join(',')

    const url = GROUP_PHOTOS.byGroup(groupId)
    const response = await executeApiRequest<GroupPhoto[]>({
      url,
      method: REQUEST_TYPE.get,
      params,
      options: { cache: 'no-store' },
    })
    return response
  } catch (error) {
    console.error('Error fetching group photos:', error)
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error occurred',
      status: 500,
    }
  }
}

export async function getGroupPhotoById(photoId: number): Promise<ApiResponse<GroupPhoto>> {
  try {
    const url = GROUP_PHOTOS.byId(photoId)
    const response = await executeApiRequest<GroupPhoto>({ url, method: REQUEST_TYPE.get })
    return response
  } catch (error) {
    console.error('Error fetching group photo:', error)
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error occurred',
      status: 500,
    }
  }
}

export async function updateGroupPhoto(
  photoId: number,
  data: { description?: string; takenAt?: string; tags?: string[] }
): Promise<ApiResponse<GroupPhoto>> {
  try {
    const url = GROUP_PHOTOS.byId(photoId)
    const response = await executeApiRequest<GroupPhoto>({
      url,
      method: REQUEST_TYPE.put,
      body: data,
    })
    return response
  } catch (error) {
    console.error('Error updating group photo:', error)
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error occurred',
      status: 500,
    }
  }
}

export async function deleteGroupPhoto(photoId: number): Promise<ApiResponse<string>> {
  try {
    const url = GROUP_PHOTOS.delete(photoId)
    const response = await executeApiRequest<string>({
      url,
      method: REQUEST_TYPE.delete,
    })
    return response
  } catch (error) {
    console.error('Error deleting group photo:', error)
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error occurred',
      status: 500,
    }
  }
}

export async function permanentlyDeleteGroupPhoto(photoId: number): Promise<ApiResponse<string>> {
  try {
    const url = GROUP_PHOTOS.deletePermanent(photoId)
    const response = await executeApiRequest<string>({
      url,
      method: REQUEST_TYPE.delete,
    })
    return response
  } catch (error) {
    console.error('Error permanently deleting group photo:', error)
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error occurred',
      status: 500,
    }
  }
}
